import { useState } from 'react'
import { IconArrowLeft, IconCheck, IconInfoCircle, IconMinus, IconShield } from '@tabler/icons-react'
import { Link, useNavigate } from 'react-router-dom'

import { authApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import { AuthFormBrand, AuthShell, StepsAside, type StepState } from '@/components/auth/AuthShell'
import { STRENGTH, strength } from '@/components/auth/passwordStrength'
import { tr } from '@/i18n/tr'
import { Alert, Button, TextField, notifications, useForm } from '@/ui'

/**
 * Parola değişimi.
 *
 * <p>Backend parola değişince TÜM oturumları (bu cihaz dahil) sonlandırır,
 * bu yüzden ardından giriş ekranına yönlendiriyoruz. Aksi halde kullanıcı
 * bir sonraki istekte beklenmedik bir 401 ile karşılaşırdı.
 *
 * <p>Giriş ekranıyla aynı iki bölümlü düzeni kullanır: ilk girişte zorunlu
 * parola değişimi yaşayan kullanıcı, kendini başka bir uygulamaya
 * düşmüş gibi hissetmesin. Sol bölüm akışın neresinde olduğunu gösterir.
 */
export function ChangePasswordPage() {
  const { user, clear } = useAuthStore()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const forced = user?.mustChangePassword ?? false

  const form = useForm({
    initialValues: { currentPassword: '', newPassword: '', repeat: '' },
    validate: {
      currentPassword: (v) => (v ? null : tr.common.required),
      newPassword: (v) => (v.length >= 10 ? null : tr.auth.passwordTooShort),
      repeat: (v, values) => (v === values.newPassword ? null : tr.auth.passwordMismatch),
    },
  })

  const submit = form.onSubmit(async (values) => {
    setBusy(true)
    setError(null)
    try {
      await authApi.changePassword(values.currentPassword, values.newPassword)
      notifications.show({
        color: 'green',
        title: 'Parolanız değiştirildi',
        message: 'Güvenlik için tüm oturumlar sonlandırıldı. Lütfen tekrar giriş yapın.',
      })
      clear()
      navigate('/login', { replace: true })
    } catch (err) {
      setError(errorMessage(err, 'Parola değiştirilemedi.'))
    } finally {
      setBusy(false)
    }
  })

  const steps: { label: string; hint: string; state: StepState }[] = forced
    ? [
      { label: 'Geçici parolayla giriş', hint: 'Yöneticinin verdiği parola ile oturum açtınız.', state: 'done' },
      { label: 'Kendi parolanızı belirleyin', hint: 'En az 10 karakter; tahmin edilmesi zor olsun.', state: 'now' },
      { label: 'Tekrar giriş yapın', hint: 'Yeni parolanızla projelerinize devam edin.', state: 'todo' },
    ]
    : [
      { label: 'Kimliğinizi doğrulayın', hint: 'Şu anki parolanızı girin.', state: 'now' },
      { label: 'Yeni parolanızı belirleyin', hint: 'En az 10 karakter; tahmin edilmesi zor olsun.', state: 'todo' },
      { label: 'Tekrar giriş yapın', hint: 'Diğer cihazlardaki oturumlarınız da kapanır.', state: 'todo' },
    ]

  const { newPassword, repeat } = form.values
  const score = strength(newPassword)
  const rules = [
    { ok: newPassword.length >= 10, label: 'En az 10 karakter' },
    { ok: newPassword.length > 0 && newPassword === repeat, label: 'Parolalar eşleşiyor' },
  ]

  return (
    <AuthShell asideLabel="Adımlar" aside={<StepsAside steps={steps} />}>
      <form className="nx-auth-form nx-auth-form--wide" onSubmit={submit} noValidate>
        <AuthFormBrand />
        {/* Zorunlu değilse kullanıcı vazgeçip geri dönebilmeli. */}
        {!forced && (
          <Link to="/" className="nx-auth-back ui-focusable">
            <IconArrowLeft size={16} stroke={1.8} aria-hidden="true" />{tr.nav.projects}
          </Link>
        )}
        <div className="nx-auth-form__head">
          <h1>{tr.auth.mustChangeTitle}</h1>
          {!forced && <p>Yeni parolanız en az 10 karakter olmalı.</p>}
        </div>

        {forced && (
          <Alert tone="lime" icon={<IconInfoCircle size={18} stroke={1.8} aria-hidden="true" />}>
            <span role="note">{tr.auth.mustChangeHint}</span>
          </Alert>
        )}
        {error && <Alert tone="danger">{error}</Alert>}

        <TextField type="password" label={tr.auth.currentPassword} size="lg" autoComplete="current-password" autoFocus
          {...form.getInputProps('currentPassword')} />
        <div className="ui-field">
          <TextField type="password" label={tr.auth.newPassword} size="lg" autoComplete="new-password"
            {...form.getInputProps('newPassword')} />
          <div className="nx-pw-meter" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => <span key={i} style={i < score ? { background: STRENGTH[score].color } : undefined} />)}
          </div>
          <span className="nx-pw-meter__label" aria-live="polite" data-good={score >= 3 ? '' : undefined}>
            Parola gücü: {STRENGTH[score].label}
          </span>
        </div>
        <TextField type="password" label={tr.auth.newPasswordAgain} size="lg" autoComplete="new-password"
          {...form.getInputProps('repeat')} />

        <ul className="nx-rules">
          {rules.map((r) => (
            <li key={r.label} data-ok={r.ok ? '' : undefined}>
              <span className="nx-rules__icon" aria-hidden="true">
                {r.ok ? <IconCheck size={11} stroke={3} /> : <IconMinus size={11} stroke={3} />}
              </span>
              <span>{r.label}</span>
              <span className="ui-visually-hidden">{r.ok ? ' (sağlandı)' : ' (sağlanmadı)'}</span>
            </li>
          ))}
        </ul>

        <Button type="submit" size="lg" full loading={busy} className="nx-auth-submit"
          leftIcon={<IconShield size={18} stroke={1.8} aria-hidden="true" />}>
          {tr.auth.changePassword}
        </Button>
      </form>
    </AuthShell>
  )
}
